import Big from 'big.js';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { UseFormClearErrors, UseFormSetError, UseFormSetValue } from 'react-hook-form';
import { getStorage } from '../storages/tpslStorage';
import { helper, format, calculator, validate, contants, TypesSymbols, TypesLeverageInfo } from '@blofin/blofin-utils';
const { numberInputFormat } = helper;
const { VaildNumber } = format;
const { toConvertNumber } = calculator;
const { MAX_PRICE_LIMIT } = validate;
const { TP_TRIGGER_PRICE_TYPE, SL_TRIGGER_PRICE_TYPE } = contants;

export type TPSLOptions = 'price' | 'pnl' | 'pnlPercentage';

function verityTriggerType(key: string): TPSLOptions {
  const type = getStorage(key);
  if (['price', 'pnl', 'pnlPercentage'].includes(type)) {
    return type;
  }
  return 'price';
}

const useCalculationWidget = ({
  marketId,
  basePrice,
  quantity,
  setValue,
  setError,
  clearErrors,
  calculationDirection,
  avgPrice,
  positionLeverage,
  buyQuantity,
  sellQuantity,
  symbolsKeyby,
  leverageInfo,
}: {
  marketId: string;
  basePrice: string;
  avgPrice?: string;
  quantity: string;
  setValue: any;
  setError: any;
  clearErrors: any;
  calculationDirection: string;
  positionLeverage?: number;
  buyQuantity?: string;
  sellQuantity?: string;
  symbolsKeyby: Record<string, TypesSymbols>;
  leverageInfo: TypesLeverageInfo | undefined;
}) => {
  const t = useTranslations('trade');

  const { leverage } = leverageInfo || { leverage: 1 };

  const calcLeverage = positionLeverage ?? leverage;

  const [tpTriggerPriceType, setTPTriggerPriceType] = useState<TPSLOptions>(verityTriggerType(TP_TRIGGER_PRICE_TYPE));
  const [slTriggerPriceType, setSLTriggerPriceType] = useState<TPSLOptions>(verityTriggerType(SL_TRIGGER_PRICE_TYPE));

  const quoteCurrency = symbolsKeyby[marketId]?.quote_currency;

  const tpsl_options = [
    {
      value: 'price',
      label: `${t('price')} (%)`,
      tooltip: t('priceTooltip'),
    },
    {
      value: 'pnl',
      label: `${t('pnl')} (${quoteCurrency})`,
      tooltip: t('pnlTooltip'),
    },
    {
      value: 'pnlPercentage',
      label: `${t('pnl')} (%)`,
      tooltip: t('pnlPercentageTooltip'),
    },
  ];

  const [tpCalculationParam, setTPCalculationParam] = useState<string>('');
  const [tpCalculationParamUSDT, setTPCalculationParamUSDT] = useState<string>('');

  const [slCalculationParam, setSLCalculationParam] = useState<string>('');
  const [slCalculationParamUSDT, setSLCalculationParamUSDT] = useState<string>('');

  const calculationValidator = {
    price: (value: string): boolean => {
      if (value === '' || value === '-') {
        return true;
      }

      const parsedValue = Number.parseFloat(value);

      return parsedValue >= -100;
    },
    pnl: (value: string): boolean => {
      return true;
    },
    pnlPercentage: (value: string): boolean => {
      return true;
      // if (value === '' || value === '-') {
      //   return true;
      // }

      // const parsedValue = Number.parseFloat(value);

      // return parsedValue >= -100;
    },
  };

  const calculationFormatter = (value: string): string => {
    if (value.startsWith('-')) {
      value = value.substring(1); // 去掉开头的负号
      const formattedValue = numberInputFormat(value, 2, false); // 格式化值
      return '-' + formattedValue; // 返回带有负号的格式化值
    } else {
      return numberInputFormat(value, 2, false); // 对非负数直接进行格式化
    }
  };

  const calculationFormula = {
    price: (value: string): string => {
      const num = new Big(1 + VaildNumber(value) / 100).times(VaildNumber(basePrice)).toString();

      const newNum = toConvertNumber(num, { precision: symbolsKeyby[marketId]?.price_digit, separator: false });

      return numberInputFormat(
        newNum,
        Number(symbolsKeyby[marketId]?.price_digit),
        false,
        Number(symbolsKeyby[marketId]?.tick_size)
      );
    },
    pnl: (value: string, sign = '+'): string => {
      if (Number(quantity) <= 0) {
        return '';
      } else {
        const num = new Big(VaildNumber(value, 1))
          .div(VaildNumber(quantity, 1))
          .div(VaildNumber(symbolsKeyby[marketId].contract_value, 1))
          .times(sign === '+' ? 1 : -1)
          .plus(avgPrice || basePrice);

        if (num.lte(0)) {
          return '0';
        }

        const newNum = toConvertNumber(num.toString(), {
          precision: symbolsKeyby[marketId]?.price_digit,
          separator: false,
        });

        return numberInputFormat(
          newNum,
          Number(symbolsKeyby[marketId]?.price_digit),
          false,
          Number(symbolsKeyby[marketId]?.tick_size)
        );
      }
    },
    pnlPercentage: (value: string, sign = '+'): string => {
      const num = new Big(VaildNumber(avgPrice || basePrice)).times(
        new Big(VaildNumber(value, 1))
          .div(100)
          .div(calcLeverage)
          .times(sign === '+' ? 1 : -1)
          .plus(1)
      );

      if (num.lte(0)) {
        return '0';
      }

      const newNum = toConvertNumber(num.toString(), {
        precision: symbolsKeyby[marketId]?.price_digit,
        separator: false,
      });
      return numberInputFormat(
        newNum,
        Number(symbolsKeyby[marketId]?.price_digit),
        false,
        Number(symbolsKeyby[marketId]?.tick_size)
      );
    },
  };

  const triggerCalculationFormula = {
    price: (value: string): string => {
      const pricePercent = new Big(VaildNumber(value, 1)).div(VaildNumber(basePrice, 1)).minus(1).times(100).toString();
      return toConvertNumber(calculationFormatter(pricePercent), {
        precision: 2,
        separator: false,
      });
    },
    pnl: (value: string, amount = ''): string => {
      if (Number(amount || quantity) <= 0) {
        return '';
      }
      let pnl = '';
      if (calculationDirection === 'SELL') {
        pnl = new Big(VaildNumber(value, 1))
          .minus(VaildNumber(avgPrice || basePrice, 1))
          .times(VaildNumber(amount || quantity, 1))
          .times(VaildNumber(symbolsKeyby[marketId].contract_value, 1))
          .times(-1)
          .toString();
      } else {
        pnl = new Big(VaildNumber(value, 1))
          .minus(VaildNumber(avgPrice || basePrice, 1))
          .times(VaildNumber(amount || quantity, 1))
          .times(VaildNumber(symbolsKeyby[marketId].contract_value, 1))
          .toString();
      }
      return toConvertNumber(pnl, {
        precision: 2,
        separator: false,
      });
    },
    pnlPercentage: (value: string): string => {
      let pnlPercent = '';
      if (calculationDirection === 'SELL') {
        pnlPercent = new Big(VaildNumber(value, 1))
          .div(VaildNumber(Number(avgPrice || basePrice), 1))
          .minus(1)
          .times(calcLeverage)
          .times(-100)
          .toString();
      } else {
        pnlPercent = new Big(VaildNumber(value, 1))
          .div(VaildNumber(Number(avgPrice || basePrice), 1))
          .minus(1)
          .times(calcLeverage)
          .times(100)
          .toString();
      }
      return toConvertNumber(pnlPercent, {
        precision: 2,
        separator: false,
      });
    },
  };

  const inverseCalculationFormula = {
    price: (value: string): string => {
      const result = new Big(VaildNumber(value)).minus(basePrice).div(VaildNumber(basePrice, 1)).times(100).toString();
      return numberInputFormat(result, 2, false);
    },
    pnl: (value: string): string => {
      if (Number(quantity) <= 0) {
        return '';
      } else {
        const contractValue = VaildNumber(symbolsKeyby[marketId]?.contract_value, 1);
        const priceDigit = Number(symbolsKeyby[marketId]?.price_digit);
        const tickSize = Number(symbolsKeyby[marketId]?.tick_size);
        const result = new Big(VaildNumber(value))
          .minus(avgPrice || basePrice)
          .times(VaildNumber(quantity, 1))
          .times(contractValue)
          .toString();
        return numberInputFormat(result, priceDigit, false, tickSize);
      }
    },
    pnlPercentage: (value: string): string => {
      const result = new Big(VaildNumber(value))
        .minus(avgPrice || basePrice)
        .div(VaildNumber(avgPrice || basePrice, 1))
        .times(calcLeverage)
        .times(100)
        .toString();
      return numberInputFormat(result, 2, false);
    },
  };

  const handleTpCalculationParamChange = (value: string, sign?: string) => {
    if (calculationValidator[tpTriggerPriceType](value)) {
      const formattedValue = calculationFormatter(value);
      setTPCalculationParam(formattedValue);
      const calculatedTpTriggerPrice = calculationFormula[tpTriggerPriceType](formattedValue, sign);
      setValue('tpTriggerPrice', calculatedTpTriggerPrice);
      if (Number(calculatedTpTriggerPrice) > MAX_PRICE_LIMIT) {
        setError('tpTriggerPrice', {
          type: 'custom',
          message: t('exceedMaxPrice'),
        });
      } else {
        clearErrors('tpTriggerPrice');
      }
    }
  };

  const handleSlCalculationParamChange = (value: string, sign?: string) => {
    if (calculationValidator[slTriggerPriceType](value)) {
      const formattedValue = calculationFormatter(value);
      setSLCalculationParam(formattedValue);
      const calculatedSlTriggerPrice = calculationFormula[slTriggerPriceType](formattedValue, sign);
      setValue('slTriggerPrice', calculatedSlTriggerPrice);
      if (Number(calculatedSlTriggerPrice) > MAX_PRICE_LIMIT) {
        setError('slTriggerPrice', {
          type: 'custom',
          message: t('exceedMaxPrice'),
        });
      } else {
        clearErrors('slTriggerPrice');
      }
    }
  };

  const tpTriggerPriceChangeCalculationParam = (value: string) => {
    if (!value) {
      setTPCalculationParam('');
      return;
    }
    if (calculationValidator[tpTriggerPriceType](value)) {
      const calculatedValue = triggerCalculationFormula[tpTriggerPriceType](value);
      // console.log('tpTriggerPriceChangeCalculationParam', tpTriggerPriceType, calculatedValue, value);
      setTPCalculationParam(calculatedValue);

      const guessCalculationDirection = new Big(VaildNumber(value, 1)).gte(VaildNumber(basePrice)) ? 'BUY' : 'SELL';
      const calculatedValueUSDT = triggerCalculationFormula['pnl'](
        value,
        guessCalculationDirection === 'BUY' ? buyQuantity : sellQuantity
      );
      setTPCalculationParamUSDT(calculatedValueUSDT);
    }
  };

  const slTriggerPriceChangeCalculationParam = (value: string) => {
    if (!value) {
      setSLCalculationParam('');
      return;
    }
    if (calculationValidator[slTriggerPriceType](value)) {
      const calculatedValue = triggerCalculationFormula[slTriggerPriceType](value);
      setSLCalculationParam(calculatedValue);

      const guessCalculationDirection = new Big(VaildNumber(value, 1)).lte(VaildNumber(basePrice)) ? 'BUY' : 'SELL';
      const calculatedValueUSDT = triggerCalculationFormula['pnl'](
        value,
        guessCalculationDirection === 'BUY' ? buyQuantity : sellQuantity
      );
      setSLCalculationParamUSDT(calculatedValueUSDT);
    }
  };

  const calcPnlWithTriggerPrice = (value: string, amount: string) => {
    return triggerCalculationFormula.pnl(value, amount);
  };

  return {
    tpsl_options,
    tpTriggerPriceType,
    setTPTriggerPriceType,
    slTriggerPriceType,
    setSLTriggerPriceType,
    tpCalculationParam,
    tpCalculationParamUSDT,
    setTPCalculationParam,
    handleTpCalculationParamChange,
    slCalculationParam,
    slCalculationParamUSDT,
    setSLCalculationParam,
    handleSlCalculationParamChange,
    inverseCalculationFormula,
    tpTriggerPriceChangeCalculationParam,
    slTriggerPriceChangeCalculationParam,
    calcPnlWithTriggerPrice,
    quoteCurrency,
  };
};

export default useCalculationWidget;
